'use client';

import { map, size } from 'lodash';
import { useMediaQuery } from 'usehooks-ts';
import Button from '@/components/shared/Button';
import Heading from '@/components/shared/Heading';
import Text from '@/components/shared/Text';
import { DownloadCard } from '@/components/ui/DownloadCard';
import { sheetStore } from '@/components/ui/ProductLayout/stores/sheetStore';
import { ScrollLayout } from '@/components/ui/ScrollArea';
import * as Sheet from '@/components/ui/Sheet';
import { CloseIcon } from '@/icons';
import { DownloadIcon } from '@vidstack/react/icons';

type RepresentativesSheetProps = {
  representativeSheet?: {
    title: string;
    description?: string;
    buttonName?: string;
    file?: {
      name: string;
      url: string;
    };
    representatives: {
      id: string;
      title: string;
      file?: {
        name: string;
        url: string;
      };
    }[];
  };
};

export const RepresentativesSheet = ({ representativeSheet }: RepresentativesSheetProps) => {
  const openSheetRepresentatives = sheetStore((state) => state.openSheetPartners);
  const setOpenSheetRepresentatives = sheetStore((state) => state.setOpenSheetPartners);
  const isMobile = useMediaQuery('(max-width: 1023px)');

  return (
    <>
      <Sheet.Root open={openSheetRepresentatives} onOpenChange={setOpenSheetRepresentatives}>
        <Sheet.Content fullScreen className="h-full overflow-y-auto lg:overflow-y-hidden">
          <ScrollLayout enabled={isMobile}>
            <div className="grid h-dvh w-full grid-cols-1 grid-rows-[auto_1fr] bg-grey-200 lg:h-full lg:grid-cols-2 lg:grid-rows-none lg:bg-transparent">
              <div className="bg-grey-200 px-6 lg:bg-grey-900/80 lg:p-12">
                <Sheet.Close className="my-6 flex w-full items-center gap-2 text-sm font-semibold uppercase leading-4 tracking-[1px] text-grey-900 focus:outline-none disabled:pointer-events-none lg:mb-5 lg:mt-0 lg:w-auto lg:text-white">
                  <span>Fechar</span>
                  <CloseIcon className="size-4" />
                  <span className="sr-only">Fechar</span>
                </Sheet.Close>
                <div className="flex flex-col justify-start gap-4 lg:max-w-2xl">
                  <Heading variant="h3" display className="lg:text-white">
                    {representativeSheet?.title}
                  </Heading>
                  {representativeSheet?.description && (
                    <Text className="lg:text-white">{representativeSheet.description}</Text>
                  )}
                  {representativeSheet?.file && (
                    <Button variant="light" className="w-fit lg:mt-1" asChild>
                      <a
                        href={representativeSheet.file.url}
                        download={representativeSheet.file.name}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {representativeSheet?.buttonName || 'Baixar'}{' '}
                        <DownloadIcon size={16} className="ml-2" />
                      </a>
                    </Button>
                  )}
                </div>
              </div>
              <ScrollLayout
                enabled={!isMobile}
                className="h-screen"
                type="auto"
                thumbClassName="bg-grey-400"
                scrollbarClassName="data-[orientation=horizontal]:h-2.5 data-[orientation=vertical]:w-2.5 2xl:data-[orientation=vertical]:w-3"
              >
                <div className="flex size-full flex-1 flex-col gap-4 bg-grey-200 px-6 py-12 lg:min-h-screen lg:p-12">
                  {size(representativeSheet?.representatives) > 0 ? (
                    map(representativeSheet?.representatives, (representative) => (
                      <DownloadCard
                        key={representative.id}
                        filePath={representative.file?.url}
                        fileName={representative.file?.name}
                        title={representative.title}
                        className="w-full"
                      />
                    ))
                  ) : (
                    <span className="text-base text-grey-700">Nenhum representante encontrado</span>
                  )}
                </div>
              </ScrollLayout>
            </div>
          </ScrollLayout>
        </Sheet.Content>
      </Sheet.Root>
    </>
  );
};
